import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, Plus, Trash2, Loader2 } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import { useToast } from '../hooks/use-toast';

const WithdrawPage = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [amount, setAmount] = useState('');
  const [withdrawPassword, setWithdrawPassword] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(null);
  const [rollover, setRollover] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch rollover/wagering status
  useEffect(() => {
    const token = localStorage.getItem('reddyannabook_token');
    if (!token) return;
    fetch('/api/rollover/status', {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.success && data.has_rollover && data.rollover) {
          const r = data.rollover;
          const required  = Number(r.rollover_required  || 0);
          const completed = Number(r.rollover_completed || 0);
          const remaining = Number(r.rollover_remaining || 0);
          setRollover({
            rollover_required:  required,
            rollover_completed: completed,
            rollover_remaining: remaining,
            percentage: required > 0 ? Math.round((completed / required) * 100) : 0,
            is_complete: remaining <= 0,
            withdrawable_amount: Number(r.withdrawable_amount || 0),
          });
        }
      })
      .catch(() => {});
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('reddyannabook_token');

      // Load current user (including demo) from storage
      let storedUser = null;
      const userData = localStorage.getItem('reddyannabook_user');
      if (userData) {
        try {
          storedUser = JSON.parse(userData);
        } catch (_) {
          storedUser = null;
        }
      }

      // If there is no real user at all, send to login
      if (!storedUser) {
        navigate('/login');
        return;
      }

      setUser(storedUser);

      // For demo user, do not require token or call wallet APIs
      if (storedUser.id === 'demo') {
        setBalance({
          main_balance: 0,
        });
        setAccounts([]);
        setLoading(false);
        return;
      }

      if (!token) {
        navigate('/login');
        return;
      }

      // Fetch balance
      const balanceResponse = await fetch(API_ENDPOINTS.WALLET_BALANCE, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (balanceResponse.ok) {
        try {
          const balanceData = await balanceResponse.json();
          if (balanceData.success) {
            setBalance(balanceData.data.balance);
          }
        } catch (e) {
          console.error('Error parsing balance response:', e);
        }
      }

      // Fetch withdrawal accounts
      const accountsResponse = await fetch(API_ENDPOINTS.WALLET_ACCOUNTS, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (accountsResponse.ok) {
        try {
          const accountsData = await accountsResponse.json();
          if (accountsData.success) {
            setAccounts(accountsData.data.accounts);
            if (accountsData.data.accounts.length > 0) {
              setSelectedAccount(accountsData.data.accounts[0]);
            }
          }
        } catch (e) {
          console.error('Error parsing accounts response:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load withdrawal data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Listen for storage changes to update accounts when new account is added
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'reddyannabook_withdraw_accounts') {
        fetchData();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const quickAdd = (value) => {
    const current = parseFloat(amount) || 0;
    setAmount((current + value).toString());
  };

  const validateWithdrawal = () => {
    const newErrors = {};
    
    if (!selectedAccount) {
      newErrors.account = 'Please select an account';
    }
    
    if (!amount || parseFloat(amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    } else {
      const withdrawAmount = parseFloat(amount);
      if (withdrawAmount < 500) {
        newErrors.amount = 'Minimum withdrawal amount is ₹500';
      }
      if (withdrawAmount > 500000) {
        newErrors.amount = 'Maximum withdrawal amount is ₹5,00,000';
      }
      // Use available balance (balance - net_exposure), not raw balance
      const availBal = (Number(user?.balance) || 0) - (Number(user?.net_exposure) || 0);
      if (user && withdrawAmount > availBal) {
        newErrors.amount = 'Insufficient available balance';
      }
    }
    
    if (!agreeTerms) {
      newErrors.terms = 'Please agree to the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProceed = async () => {
    // Check for existing pending withdrawal first
    try {
      const token = localStorage.getItem('reddyannabook_token');
      if (token) {
        const checkRes = await fetch(API_ENDPOINTS.WALLET_CHECK_PENDING, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        const checkData = await checkRes.json();
        if (checkData.data?.has_pending_withdrawal) {
          setShowPendingModal({ type: 'withdrawal', message: checkData.data.message });
          return;
        }
      }
    } catch {}

    const newErrors = {};

    if (!amount || parseFloat(amount) < 500 || parseFloat(amount) > 500000) {
      newErrors.amount = 'Amount must be between ₹500 and ₹5,00,000';
    }
    
    if (!selectedAccount) {
      newErrors.account = 'Please select a withdrawal account';
    }
    
    if (!withdrawPassword) {
      newErrors.password = 'Please enter withdrawal password';
    }
    
    if (!agreeTerms) {
      newErrors.terms = 'Please agree to the terms and conditions';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Check sufficient AVAILABLE balance (main_balance - exposure)
    const availForCheck = balance
      ? (Number(balance.available_credit ?? (balance.main_balance - (balance.net_exposure || balance.exposure || 0))) || 0)
      : ((Number(user?.balance) || 0) - (Number(user?.net_exposure) || 0));
    if (availForCheck < parseFloat(amount)) {
      newErrors.amount = 'Insufficient available balance';
      setErrors(newErrors);
      return;
    }
    
    setSubmitting(true);
    setErrors({});

    try {
      const token = localStorage.getItem('reddyannabook_token');
      // For demo user, do not redirect to login; just show message
      if (!token) {
        if (user && user.id === 'demo') {
          setErrors({ general: 'Withdrawals are not available in Demo mode.' });
          toast({
            title: 'Demo Mode',
            description: 'Withdrawals are disabled for demo accounts.',
            variant: 'destructive',
          });
          return;
        }
        navigate('/login');
        return;
      }

      const requestData = {
        amount: parseFloat(amount),
        account_id: selectedAccount.id,
        withdraw_password: withdrawPassword
      };

      const response = await fetch(API_ENDPOINTS.WALLET_SUBMIT_WITHDRAWAL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        let errorMessage = 'Failed to submit withdrawal request';
        if (response.status === 401) {
          errorMessage = 'Your session has expired. Please log in again.';
        } else {
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch (e) {
            errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          }
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Success",
          description: data.data.message || "Withdrawal request submitted successfully!",
        });
        navigate('/wallet');
      } else {
        throw new Error(data.message || 'Failed to submit withdrawal request');
      }
    } catch (err) {
      setErrors({ general: err.message || 'Failed to submit withdrawal request' });
      toast({
        title: "Error",
        description: err.message || 'Failed to submit withdrawal request',
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddAccount = () => {
    navigate('/add-account');
  };

  const handleDeleteAccount = async (accountId) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      try {
        const token = localStorage.getItem('reddyannabook_token');
        if (!token) {
          toast({
            title: 'Error',
            description: 'Please login to delete account',
            variant: 'destructive'
          });
          return;
        }

        const response = await fetch(`${API_ENDPOINTS.WALLET_ACCOUNTS}/${accountId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (data.success) {
          // Remove account from local state
          const updatedAccounts = accounts.filter(account => account.id !== accountId);
          setAccounts(updatedAccounts);
          
          // If deleted account was selected, select first available or none
          if (selectedAccount?.id === accountId) {
            setSelectedAccount(updatedAccounts.length > 0 ? updatedAccounts[0] : null);
          }

          toast({
            title: 'Success',
            description: 'Account deleted successfully',
            variant: 'default'
          });
        } else {
          toast({
            title: 'Error',
            description: data.message || 'Failed to delete account',
            variant: 'destructive'
          });
        }
      } catch (error) {
        console.error('Error deleting account:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete account. Please try again.',
          variant: 'destructive'
        });
      }
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-6 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-600" />
          <p className="text-gray-600">Loading withdrawal data...</p>
        </div>
      </div>
    );
  }

  // No accounts state
  if (accounts.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-6">
        <div className="max-w-md mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-bold text-gray-900">Withdraw funds</h1>
            <div className="flex items-center gap-3">
              <Button onClick={handleAddAccount} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2">
                Add New Account
              </Button>
              <div className="flex items-center gap-1 text-gray-700">
                <span>Help</span>
                <HelpCircle className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* No Account Message */}
          <div className="bg-white border-2 border-gray-900 rounded-lg p-4">
            <p className="font-bold text-gray-900">No Account Available. Continue with new account!!</p>
          </div>

          {/* Rules */}
          <div className="bg-white border-2 border-gray-900 rounded-lg p-4">
            <ol className="space-y-2 text-sm text-gray-900">
              <li><strong>1.</strong> This form is for withdrawing the amount from the main wallet only.</li>
              <li><strong>2.</strong> The bonus wallet amount cannot be withdrawn by this form.</li>
              <li><strong>3.</strong> Do not put Withdraw request without betting with deposit amount. Such activity may be identified as Suspicious</li>
              <li><strong>4.</strong> If multiple users are using same withdraw account then all the linked users will be blocked.</li>
              <li><strong>5.</strong> Maximum Withdraw time is 45 minutes then only complain on WhatsApp number.</li>
              <li><strong>6.</strong> Withdrawal account should be same to deposit account.</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  // Has accounts state
  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6 pb-32">
      <div className="max-w-md mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-900">Withdraw funds</h1>
          <div className="flex items-center gap-3">
            <Button onClick={handleAddAccount} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2">
              Create New Account
            </Button>
            <div className="flex items-center gap-1 text-green-600">
              <span>Help?</span>
              <HelpCircle className="w-4 h-4" />
            </div>
          </div>
        </div>

        {/* Rollover / Wagering Progress */}
        {rollover && !rollover.is_complete && (
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '16px',
            border: '1px solid #fbbf24',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontWeight: '600', fontSize: '14px' }}>🎯 Rollover Progress</span>
              <span style={{ fontSize: '13px', color: '#64748b' }}>{rollover.percentage}%</span>
            </div>
            <div style={{ background: '#e2e8f0', borderRadius: '999px', height: '8px', marginBottom: '8px', overflow: 'hidden' }}>
              <div style={{
                background: 'linear-gradient(90deg, #1e8067, #22c55e)',
                borderRadius: '999px',
                height: '100%',
                width: `${Math.min(100, Math.max(0, Number(rollover.percentage) || 0))}%`,
                transition: 'width 0.3s',
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#64748b' }}>
              <span>Completed: ₹{Number(rollover.rollover_completed || 0).toLocaleString('en-IN')}</span>
              <span>Required: ₹{Number(rollover.rollover_required || 0).toLocaleString('en-IN')}</span>
            </div>
            <div style={{ marginTop: '8px', padding: '8px', background: '#fef3c7', borderRadius: '8px', fontSize: '12px', color: '#92400e' }}>
              ⚠️ Complete rollover to withdraw. Remaining: ₹{Number(rollover.rollover_remaining || 0).toLocaleString('en-IN')}
            </div>
          </div>
        )}
        {rollover && rollover.is_complete && (
          <div style={{
            background: '#f0fdf4',
            border: '1px solid #86efac',
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#15803d',
            fontWeight: '600',
          }}>
            ✅ Rollover complete! You can withdraw freely.
          </div>
        )}

        {/* Choose Account */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Choose From Recent Accounts</h2>
          {accounts.map((account) => (
            <div
              key={account.id}
              className={`border-2 rounded-lg p-4 cursor-pointer ${
                selectedAccount?.id === account.id ? 'border-green-500' : 'border-gray-200'
              }`}
              onClick={() => setSelectedAccount(account)}
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 ${
                  selectedAccount?.id === account.id ? 'bg-green-500 border-green-500' : 'border-gray-300'
                }`}>
                  {selectedAccount?.id === account.id && (
                    <div className="w-full h-full rounded-full bg-white scale-50"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="text-sm space-y-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                        (account.bank_name || '').includes('UPI') ? 'bg-purple-100 text-purple-700' :
                        ['PhonePe','Google Pay','Paytm'].includes(account.bank_name) ? 'bg-indigo-100 text-indigo-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {(account.bank_name || '').includes('UPI') ? 'UPI' :
                         ['PhonePe','Google Pay','Paytm'].includes(account.bank_name) ? account.bank_name :
                         'Bank'}
                      </span>
                      <strong>{account.bank_name || account.bankName}</strong>
                    </div>
                    <div><strong>Account/ID:</strong> {account.account_number || account.accountNumber}</div>
                    {account.ifsc_code && !['UPI','PHONEPE','GOOGLEPAY','PAYTM'].includes(account.ifsc_code) && (
                      <div><strong>IFSC:</strong> {account.ifsc_code || account.ifscCode}</div>
                    )}
                    <div className="text-xs text-gray-500">{account.account_holder_name || account.accountHolderName}</div>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteAccount(account.id)}
                  className="p-1 hover:bg-red-100 rounded text-red-500 hover:text-red-700"
                  title="Delete account"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Withdraw Amount */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Withdraw Amount</h2>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                className="h-12 flex-1"
                min="100"
                max="500000"
              />
              <div className="bg-green-100 text-green-800 px-3 py-2 rounded-md text-sm font-medium">
                Avail Bal: ₹{(() => {
                  const avail = balance
                    ? Number(balance.available_credit ?? (Number(balance.main_balance || 0) - Number(balance.net_exposure ?? balance.exposure ?? 0)))
                    : ((Number(user?.balance) || 0) - (Number(user?.net_exposure) || 0));
                  return Number(avail || 0).toLocaleString('en-IN');
                })()}
              </div>
            </div>

            <div className="text-sm text-gray-600">
              Min ₹500 | Max ₹5,00,000
            </div>
            
            <div className="grid grid-cols-4 gap-3">
              <Button
                onClick={() => quickAdd(100)}
                className="bg-green-100 hover:bg-green-200 text-green-800 h-10"
              >
                +100
              </Button>
              <Button
                onClick={() => quickAdd(200)}
                className="bg-green-100 hover:bg-green-200 text-green-800 h-10"
              >
                +200
              </Button>
              <Button
                onClick={() => quickAdd(500)}
                className="bg-green-100 hover:bg-green-200 text-green-800 h-10"
              >
                +500
              </Button>
              <Button
                onClick={() => quickAdd(1000)}
                className="bg-green-100 hover:bg-green-200 text-green-800 h-10"
              >
                +1000
              </Button>
            </div>
            
            {errors.amount && (
              <p className="text-red-600 text-sm">{errors.amount}</p>
            )}
          </div>
        </div>

        {/* Withdrawal Password */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Password Verification</h2>
          <Input
            type="password"
            value={withdrawPassword}
            onChange={(e) => setWithdrawPassword(e.target.value)}
            placeholder="Enter your login password"
            className="h-12"
          />
          <p className="text-sm text-gray-600 mt-2">
            Enter your account login password to confirm this withdrawal
          </p>
          {errors.password && (
            <p className="text-red-600 text-sm mt-2">{errors.password}</p>
          )}
        </div>

        {/* Terms */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm text-gray-700">
              I have read and agree with the{' '}
              <span className="underline text-green-600">terms of payment</span> and{' '}
              <span className="underline text-green-600">withdrawal policy</span>
            </span>
          </label>
          {errors.terms && (
            <p className="text-red-600 text-sm mt-2">{errors.terms}</p>
          )}
        </div>

        {/* General Error */}
        {errors.general && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700 text-sm">{errors.general}</p>
          </div>
        )}

        {/* Proceed Button */}
        <Button
          onClick={handleProceed}
          disabled={submitting}
          className="w-full bg-green-600 hover:bg-green-700 text-white h-12"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            'Proceed'
          )}
        </Button>

        {/* Rules */}
        <div className="bg-white border-2 border-gray-900 rounded-lg p-4">
          <ol className="space-y-2 text-sm text-gray-900">
            <li><strong>1.</strong> This form is for withdrawing the amount from the main wallet only.</li>
            <li><strong>2.</strong> The bonus wallet amount cannot be withdrawn by this form.</li>
            <li><strong>3.</strong> Do not put Withdraw request without betting with deposit amount. Such activity may be identified as Suspicious</li>
            <li><strong>4.</strong> If multiple users are using same withdraw account then all the linked users will be blocked.</li>
            <li><strong>5.</strong> Maximum Withdraw time is 45 minutes then only complain on WhatsApp number.</li>
            <li><strong>6.</strong> Withdrawal account should be same to deposit account.</li>
          </ol>
        </div>

        {/* Withdrawal History */}
        <WithdrawalHistory />
      </div>

      {/* Pending Modal */}
      {showPendingModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center shadow-2xl">
            <div className="text-5xl mb-4">&#9203;</div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Withdrawal Pending</h2>
            <p className="text-gray-500 text-sm mb-4">
              {showPendingModal.message || 'You already have a pending withdrawal request. Please wait for it to be approved or cancel it before making a new request.'}
            </p>
            <div className="space-y-2">
              <button
                onClick={() => { setShowPendingModal(null); navigate('/wallet'); }}
                className="w-full py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors"
              >
                View My Requests
              </button>
              <button
                onClick={() => setShowPendingModal(null)}
                className="w-full py-2 text-gray-400 text-sm hover:text-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const WithdrawalHistory = () => {
  const [txs, setTxs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('reddyannabook_token');
        const API_BASE = '';
        const res = await fetch(`${API_BASE}/api/wallet/transactions?type=withdrawal`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setTxs((data.data || []).filter(t => t.type === 'withdrawal' || t.type === 'coin_recovery').slice(0, 15));
      } catch {}
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="text-center py-4 text-gray-400 text-sm">Loading history...</div>;
  if (txs.length === 0) return null;

  const fmtDate = (d) => { if (!d) return ''; try { const s = String(d).trim(); const utc = (s.includes('T')||s.includes('Z')||s.includes('+')) ? s : s.replace(' ','T')+'Z'; return new Date(utc).toLocaleDateString('en-IN', { timeZone:'Asia/Kolkata', day:'numeric', month:'short' }); } catch { return ''; } };
  const badge = (s) => {
    if (s === 'completed') return <span className="text-[10px] font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Approved</span>;
    if (s === 'pending') return <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Pending</span>;
    if (s === 'cancelled') return <span className="text-[10px] font-bold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">Cancelled</span>;
    return <span className="text-[10px] font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">Rejected</span>;
  };

  return (
    <div className="mt-6">
      <h3 className="text-sm font-bold text-gray-700 mb-2">Withdrawal History</h3>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-4 gap-0 px-3 py-2 bg-gray-50 text-[10px] font-bold text-gray-500 uppercase border-b">
          <div>Date</div><div>Amount</div><div>Method</div><div>Status</div>
        </div>
        {txs.map(tx => {
          const isRecovery = tx.type === 'coin_recovery';
          return (
            <div key={`${tx.type}-${tx.id}`} className="grid grid-cols-4 gap-0 px-3 py-2 border-b border-gray-50 text-xs">
              <div className="text-gray-600">{fmtDate(tx.created_at)}</div>
              <div className={`font-bold ${isRecovery ? 'text-red-700' : 'text-gray-900'}`}>₹{Math.abs(parseFloat(tx.amount)).toLocaleString('en-IN')}</div>
              <div className="text-gray-600 capitalize">{isRecovery ? '🔴 Master Debit' : (tx.method || 'Bank')}</div>
              <div>{badge(tx.status)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WithdrawPage;
